import { useRouter } from 'next/router';

import { LOCATIONS_URL, PHYSICIANS_URL } from '@/lib/constants';
import { useOasApp } from '@/providers/oas-app-provider';

type RouterPush =
  | string
  | {
      pathname: string;
      query?: Record<string, string>;
    };

export const useCustomRouting = (): { routerPush: (url: RouterPush) => void } => {
  const { isLocation, dotcms, scSiteCookie } = useOasApp();
  const router = useRouter();

  // Custom routing logic for location level sites
  // that preserves url segments as we need to do a full page reload
  const routerPush = (url: RouterPush) => {
    const isLocationFallback = window.location.pathname.includes(LOCATIONS_URL);

    // Note: special handling for dotcms to preserve url segments
    if (scSiteCookie?.toLowerCase() === 'dotcms' || dotcms?.dotCmsHostId) {
      const trimmedPath = window.location.pathname.split(PHYSICIANS_URL)[1] || '';
      // We remove the last segment as it's the current page being navigated away from
      // and keep the /physicians and location segments for correct routing

      // Example: 'aventura-hospital'
      const locationSlug = isLocationFallback
        ? trimmedPath.split(LOCATIONS_URL)[1]?.split('/')[1]
        : '';
      // Example: '/locations/aventura-hospital'
      const fullLocationPrefixedPath = `${LOCATIONS_URL}/${locationSlug}`;
      const routerPath = typeof url === 'string' ? url : url.pathname;
      const dotCMSPath = locationSlug ? fullLocationPrefixedPath + routerPath : routerPath;

      const pushUrl = {
        pathname: dotCMSPath,
        query: typeof url === 'string' ? undefined : url.query,
      };

      router.push(pushUrl);
      return;
    }

    if (isLocation || isLocationFallback) {
      const path =
        typeof url === 'string'
          ? url
          : `${url.pathname}?${new URLSearchParams(url.query).toString()}`;

      window.location.href = `${location.origin}${path.startsWith('/') ? path : '/' + path}`;
      return;
    }
    router.push(url);
  };

  return {
    routerPush,
  };
};
